import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { sucursalService, Sucursal, SucursalCreate, SucursalUpdate } from '../../services/sucursalService';
import { tenantService } from '../../services/tenantService';
import { useAuthStore } from '../../store/authStore';

interface SucursalFormProps {
  sucursal?: Sucursal | null;
  onClose: () => void;
}

const SucursalForm = ({ sucursal, onClose }: SucursalFormProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  
  const { data: tenantsData } = useQuery(
    'tenants',
    () => tenantService.getAll(1, 100)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<SucursalCreate | SucursalUpdate>({
    defaultValues: sucursal || {
      name: '',
      address: '',
      phone: '',
      email: '',
      timezone: 'America/Mexico_City',
      tenantId: user?.tenantId
    }
  });

  useEffect(() => {
    if (sucursal) {
      reset(sucursal);
    }
  }, [sucursal, reset]);

  const createMutation = useMutation(sucursalService.create, {
    onSuccess: () => {
      toast.success('Sucursal creada correctamente');
      queryClient.invalidateQueries(['sucursales']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear sucursal');
    }
  });

  const updateMutation = useMutation(
    (data: SucursalUpdate) => sucursalService.update(sucursal!.id, data),
    {
      onSuccess: () => {
        toast.success('Sucursal actualizada correctamente');
        queryClient.invalidateQueries(['sucursales']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar sucursal');
      }
    }
  );

  const onSubmit = (data: SucursalCreate | SucursalUpdate) => {
    if (sucursal) {
      updateMutation.mutate(data as SucursalUpdate);
    } else {
      // Asegurar que tenantId esté presente
      const createData: SucursalCreate = {
        ...data as SucursalCreate,
        tenantId: data.tenantId || user?.tenantId
      };
      
      if (!createData.tenantId) {
        toast.error('Tenant ID es requerido');
        return;
      }
      
      createMutation.mutate(createData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {sucursal ? 'Editar Sucursal' : 'Nueva Sucursal'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {user?.role === 'admin' && tenantsData && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tenant
              </label>
              <select
                {...register('tenantId', { valueAsNumber: true })}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {tenantsData.data.tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <input
              {...register('name', { required: 'Nombre es requerido' })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dirección
            </label>
            <textarea
              {...register('address')}
              rows={3}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teléfono
            </label>
            <input
              {...register('phone')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              {...register('email')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone
            </label>
            <select
              {...register('timezone')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="America/Mexico_City">America/Mexico_City</option>
              <option value="America/New_York">America/New_York</option>
              <option value="America/Los_Angeles">America/Los_Angeles</option>
            </select>
          </div>

          {sucursal && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Activa</span>
              </label>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SucursalForm;

